import React, { useState, useEffect } from 'react';
import { GAME_CATALOG, type GameMetadata, type GameHubUser, type HubStarsProduct } from '@gamengayxua/shared';
import { api } from './api/client.ts';
import { OneLifeGame } from './components/games/OneLifeGame.tsx';
import { GalaxyGame } from './components/games/GalaxyGame.tsx';
import { SuQuanGame } from './components/games/SuQuanGame.tsx';
import { NinjaGame } from './components/games/NinjaGame.tsx';
import { TamQuocGame } from './components/games/TamQuocGame.tsx';
import { XiangqiGame } from './components/games/XiangqiGame.tsx';
import { ChessGame } from './components/games/ChessGame.tsx';
import { BibleQuizGame } from './components/games/BibleQuizGame.tsx';

declare global {
  interface Window {
    Telegram?: {
      WebApp?: any;
    };
  }
}

type TabType = 'home' | 'leaderboard' | 'quests' | 'referral' | 'shop' | 'profile';

export const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('home');
  const [currentGameId, setCurrentGameId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [user, setUser] = useState<GameHubUser | null>(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState<boolean>(true);
  const [starsProducts, setStarsProducts] = useState<HubStarsProduct[]>([]);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [quests, setQuests] = useState<any[]>([]);
  const [copiedRef, setCopiedRef] = useState<boolean>(false);
  const [purchasingProduct, setPurchasingProduct] = useState<string | null>(null);
  const [shopFeedback, setShopFeedback] = useState<string | null>(null);

  // Initialize Telegram WebApp & Auth
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready?.();
      tg.expand?.();
      tg.setHeaderColor?.('#070b14');
      tg.setBackgroundColor?.('#070b14');
    }

    const initData = tg?.initData || '';
    const startParam = tg?.initDataUnsafe?.start_param || '';

    // Handle deep-link to a specific game
    const urlParams = new URLSearchParams(window.location.search);
    const urlGame = urlParams.get('game') || (window.location.hash.startsWith('#game=') ? window.location.hash.substring(6) : null);
    if (urlGame) {
      setCurrentGameId(urlGame);
    } else if (startParam.startsWith('game_')) {
      const gId = startParam.substring(5);
      setCurrentGameId(gId);
    } else if (startParam.startsWith('life_')) {
      setCurrentGameId('one-life');
    }

    const authenticate = async () => {
      try {
        if (initData) {
          const res = await api.loginTelegram(initData, startParam);
          setUser(res.user);
        } else {
          // Fallback demo for browser development
          const res = await api.loginDemo('Chỉ Huy Hoài Cổ', 600094025);
          setUser(res.user);
        }
      } catch (err) {
        console.warn('Auth fallback:', err);
        const res = await api.loginDemo('Khách Trải Nghiệm', 999111222);
        setUser(res.user);
      } finally {
        setIsLoadingAuth(false);
      }
    };

    authenticate();
  }, []);

  // Load secondary data
  useEffect(() => {
    api.getStarsProducts().then((res) => setStarsProducts(res.products)).catch(() => {});
    api.getLeaderboard().then((res) => setLeaderboard(res.leaderboard)).catch(() => {});
    api.getDailyQuests().then((res) => setQuests(res.quests)).catch(() => {});
  }, [user]);

  const filteredGames = selectedCategory === 'all'
    ? GAME_CATALOG
    : GAME_CATALOG.filter((g) => g.category === selectedCategory);

  const handleOpenGame = (gameId: string) => {
    setCurrentGameId(gameId);
    api.saveGameProgress(gameId, {}, 'Đã mở game').catch(() => {});
  };

  const handleBuyStars = async (product: HubStarsProduct) => {
    setPurchasingProduct(product.id);
    setShopFeedback(null);
    try {
      const res = await api.createStarsInvoice(product.id);
      if (res.invoiceLink && window.Telegram?.WebApp?.openInvoice) {
        window.Telegram.WebApp.openInvoice(res.invoiceLink, (status: string) => {
          if (status === 'paid') {
            setShopFeedback(`🎉 Thanh toán thành công [${product.name}]!`);
          }
        });
      } else {
        setShopFeedback(`Đã tạo đơn #${res.orderId}. Vui lòng mở qua Telegram để thanh toán!`);
      }
    } catch (err: any) {
      setShopFeedback(`Lỗi: ${err.message}`);
    } finally {
      setPurchasingProduct(null);
    }
  };

  // Render Active Sub-game if open
  if (currentGameId) {
    if (currentGameId === 'one-life') {
      return <OneLifeGame onBackToHub={() => setCurrentGameId(null)} />;
    }
    if (currentGameId === 'galaxy-empire') {
      return <GalaxyGame onBackToHub={() => setCurrentGameId(null)} />;
    }
    if (currentGameId === 'su-quan') {
      return <SuQuanGame onBackToHub={() => setCurrentGameId(null)} />;
    }
    if (currentGameId === 'ninja-legacy') {
      return <NinjaGame onBackToHub={() => setCurrentGameId(null)} />;
    }
    if (currentGameId === 'tam-quoc') {
      return <TamQuocGame onBackToHub={() => setCurrentGameId(null)} />;
    }
    if (currentGameId === 'xiangqi') {
      return <XiangqiGame onBackToHub={() => setCurrentGameId(null)} />;
    }
    if (currentGameId === 'chess') {
      return <ChessGame onBackToHub={() => setCurrentGameId(null)} />;
    }
    if (currentGameId === 'bible-quiz') {
      return <BibleQuizGame onBackToHub={() => setCurrentGameId(null)} />;
    }
  }

  return (
    <div className="app-container max-w-md mx-auto space-y-4 font-sans text-slate-100">
      {/* 1. TOP PLAYER HUD */}
      <header
        onClick={() => setActiveTab('profile')}
        className="retro-card p-3 flex items-center justify-between border-slate-700/50 shadow-lg cursor-pointer active:scale-[0.99] transition-transform"
      >
        <div className="flex items-center space-x-2.5">
          <div className="relative">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-tr from-cyan-500 via-indigo-600 to-amber-500 flex items-center justify-center text-xl font-black shadow-md">
              🕹️
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-[#0d1424]" />
          </div>
          <div className="space-y-0.5">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-black tracking-wide text-white">
                {user ? user.firstName || user.username : 'Chỉ Huy'}
              </span>
              <span className="text-[11px] px-2 py-0.5 rounded font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                Lv.{user?.level || 1}
              </span>
            </div>
            <p className="text-xs text-slate-300 font-medium">
              Game Ngày Xưa Hub • @Gamengayxua_bot
            </p>
          </div>
        </div>

        <div className="text-right space-y-1">
          <div className="flex items-center justify-end space-x-1">
            <span className="text-xs text-cyan-400 font-mono font-bold">{(user?.exp || 0)} EXP</span>
          </div>
          <div className="w-24 h-2 bg-slate-900 rounded-full overflow-hidden border border-slate-800">
            <div
              className="h-full bg-gradient-to-r from-cyan-400 to-amber-400 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, Math.max(12, ((user?.exp || 0) % 200) / 2))}%` }}
            />
          </div>
        </div>
      </header>

      {/* 2. TAB: HOME */}
      {activeTab === 'home' && (
        <div className="space-y-4">
          {/* Spotlight Hero: Đế Chế Thiên Hà */}
          <div className="retro-card-featured p-4 sm:p-5 rounded-2xl relative overflow-hidden space-y-3.5">
            <div className="absolute -right-3 -bottom-3 text-7xl opacity-15 pointer-events-none select-none">
              🚀
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[11px] px-3 py-1 rounded-full font-black tracking-wider uppercase bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">
                ⭐ TIÊU ĐIỂM HÔM NAY
              </span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 font-bold border border-emerald-500/30">
                🟢 FULL ENGINE 4X
              </span>
            </div>

            <div className="space-y-1.5">
              <h2 className="text-xl font-black text-white tracking-wide">
                Đế Chế Thiên Hà
              </h2>
              <p className="text-sm text-slate-200 leading-relaxed font-normal">
                Chiến thuật không gian thời gian thực: Khai thác tài nguyên, nghiên cứu công nghệ, đóng hạm đội & liên minh tác chiến PvP.
              </p>
            </div>

            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center space-x-2 text-xs text-slate-300 font-medium">
                <span>⭐ 4.9</span>
                <span>•</span>
                <span>8.9k người chơi</span>
              </div>
              <button
                onClick={() => handleOpenGame('galaxy-empire')}
                className="px-4 py-2 text-xs sm:text-sm font-black retro-button-cyan uppercase tracking-wider flex items-center space-x-1.5 shadow-md shadow-cyan-500/20 active:scale-95 transition-transform"
              >
                <span>CHƠI NGAY</span>
                <span>→</span>
              </button>
            </div>
          </div>

          {/* Category Filter Bar */}
          <div className="flex space-x-2 overflow-x-auto pb-1 text-xs sm:text-sm no-scrollbar">
            {[
              { id: 'all', label: 'Tất Cả', icon: '🎮' },
              { id: 'strategy', label: 'Chiến Thuật', icon: '⚔️' },
              { id: 'simulation', label: 'Nhân Sinh', icon: '👶' },
              { id: 'board_game', label: 'Cờ Trí Tuệ', icon: '♟️' },
              { id: 'rpg_action', label: 'Ninja 2D', icon: '🥷' },
              { id: 'quiz_puzzle', label: 'Đố Vui', icon: '📖' },
            ].map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3.5 py-2 rounded-xl font-bold whitespace-nowrap transition-all flex items-center space-x-1.5 ${
                  selectedCategory === cat.id
                    ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-black shadow-md shadow-amber-500/20 scale-[1.02]'
                    : 'bg-slate-900/90 text-slate-300 hover:text-white border border-slate-800'
                }`}
              >
                <span>{cat.icon}</span>
                <span>{cat.label}</span>
              </button>
            ))}
          </div>

          {/* Game List Cards */}
          <div className="space-y-3.5">
            {filteredGames.map((game) => (
              <div
                key={game.id}
                className="retro-card p-4 space-y-3 transition-all hover:border-slate-600/60"
              >
                {/* Header row: Icon + Info + Tier Badge */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="w-12 h-12 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-2xl shadow-inner flex-shrink-0">
                      {game.icon}
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-black text-white truncate">
                        {game.name}
                      </h3>
                      <p className="text-xs text-slate-300 font-medium truncate">
                        {game.categoryName} • ⭐ {game.rating}
                      </p>
                    </div>
                  </div>

                  {/* Clean Single Maturity Badge */}
                  <span className={`text-xs px-2.5 py-1 rounded-full font-black tracking-wide flex-shrink-0 ${
                    game.maturityTier === 'FULL PLAYABLE'
                      ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                      : game.maturityTier === 'MVP'
                      ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/30'
                      : 'bg-purple-500/15 text-purple-300 border border-purple-500/30'
                  }`}>
                    {game.maturityTier}
                  </span>
                </div>

                {/* Short Hook Description */}
                <p className="text-sm text-slate-200 leading-relaxed font-normal line-clamp-2">
                  {game.description}
                </p>

                {/* Footer row: Tags + Play Button */}
                <div className="flex items-center justify-between pt-1 gap-2 border-t border-slate-800/60">
                  <div className="flex flex-wrap gap-1.5 max-w-[58%]">
                    {game.tags.slice(0, 2).map((t) => (
                      <span
                        key={t}
                        className="text-xs px-2.5 py-0.5 rounded-md bg-slate-900/90 text-slate-300 border border-slate-800 whitespace-nowrap"
                      >
                        {t}
                      </span>
                    ))}
                  </div>

                  <button
                    onClick={() => handleOpenGame(game.id)}
                    className={`px-4 py-2 text-xs sm:text-sm font-black uppercase tracking-wider flex items-center space-x-1.5 flex-shrink-0 ${
                      game.id === 'galaxy-empire' ? 'retro-button-cyan' : 'retro-button'
                    }`}
                  >
                    <span>CHƠI NGAY</span>
                    <span>→</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 3. TAB: LEADERBOARD */}
      {activeTab === 'leaderboard' && (
        <div className="retro-card p-4 space-y-4">
          <div className="text-center space-y-1">
            <span className="text-3xl">🏆</span>
            <h3 className="text-base font-black text-amber-400">BẢNG XẾP HẠNG HUYỀN THOẠI</h3>
            <p className="text-xs text-slate-400">Vinh danh các cao thủ trong hệ sinh thái Game Ngày Xưa</p>
          </div>

          <div className="space-y-2">
            {leaderboard.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-400 bg-slate-900/50 rounded-xl border border-slate-800">
                Đang tải dữ liệu cao thủ...
              </div>
            ) : (
              leaderboard.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-xl bg-slate-900/80 border border-slate-800 text-xs"
                >
                  <div className="flex items-center space-x-3">
                    <span className={`w-6 text-center font-black ${
                      idx === 0 ? 'text-amber-400 text-base' : idx === 1 ? 'text-slate-300 text-base' : idx === 2 ? 'text-amber-600 text-base' : 'text-slate-500'
                    }`}>
                      {idx + 1}
                    </span>
                    <div>
                      <p className="font-bold text-slate-200">{item.first_name || item.username}</p>
                      <p className="text-[10px] text-slate-400">@{item.username}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-amber-400 font-bold">{item.exp} EXP</span>
                    <p className="text-[10px] text-slate-400">Cấp {item.level}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* 4. TAB: QUESTS */}
      {activeTab === 'quests' && (
        <div className="retro-card p-4 space-y-4">
          <div className="text-center space-y-1">
            <span className="text-3xl">🎁</span>
            <h3 className="text-base font-black text-amber-400">NHIỆM VỤ & QUÀ HẰNG NGÀY</h3>
            <p className="text-xs text-slate-400">Hoàn thành các mốc trải nghiệm để thăng cấp tài khoản</p>
          </div>

          <div className="space-y-2.5">
            {quests.map((q) => (
              <div
                key={q.id}
                className="p-3.5 rounded-xl bg-slate-900/80 border border-slate-800 text-xs flex items-center justify-between"
              >
                <div className="space-y-1">
                  <p className="font-bold text-slate-200">{q.title}</p>
                  <p className="text-[11px] text-slate-400">{q.description}</p>
                  <span className="text-[10px] text-amber-400 font-semibold">+{q.rewardExp} EXP</span>
                </div>
                <button
                  disabled={!q.completed || q.claimed}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    q.claimed
                      ? 'bg-slate-800 text-slate-500'
                      : q.completed
                      ? 'retro-button'
                      : 'bg-slate-800 text-slate-400'
                  }`}
                >
                  {q.claimed ? 'Đã Nhận' : q.completed ? 'Nhận Quà' : `${q.progress}/${q.target}`}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 5. TAB: REFERRAL */}
      {activeTab === 'referral' && (
        <div className="retro-card p-5 space-y-4 text-center">
          <span className="text-4xl">🤝</span>
          <div className="space-y-1">
            <h3 className="text-base font-black text-amber-400">MỜI BẠN NHẬN THƯỞNG</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              Cùng bạn bè ôn lại ký ức tuổi thơ. Nhận ngoại trang độc quyền và lượt xuất album kiếp người miễn phí!
            </p>
          </div>

          <div className="bg-slate-900/90 p-3 rounded-xl border border-slate-800 space-y-2 text-left">
            <label className="text-[11px] text-slate-400 font-semibold block">Mã giới thiệu của bạn:</label>
            <div className="flex items-center space-x-2">
              <input
                readOnly
                value={`https://t.me/Gamengayxua_bot?startapp=ref_${user?.referralCode || 'VIP'}`}
                className="flex-1 bg-slate-950 p-2 rounded-lg border border-slate-800 text-xs text-amber-300 font-mono"
              />
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(`https://t.me/Gamengayxua_bot?startapp=ref_${user?.referralCode || 'VIP'}`);
                  setCopiedRef(true);
                  setTimeout(() => setCopiedRef(false), 2000);
                }}
                className="px-3 py-2 text-xs font-bold retro-button whitespace-nowrap"
              >
                {copiedRef ? 'Đã chép!' : 'Sao chép'}
              </button>
            </div>
          </div>

          {/* Milestones */}
          <div className="space-y-2 text-left text-xs">
            <h4 className="font-bold text-slate-200">MỐC PHẦN THƯỞNG GIỚI THIỆU:</h4>
            <div className="space-y-1.5">
              <div className="p-2.5 rounded-lg bg-slate-900/60 border border-slate-800 flex justify-between">
                <span>Mốc 1 người:</span>
                <span className="font-bold text-amber-300">Huy hiệu Kết Nối + 100 EXP</span>
              </div>
              <div className="p-2.5 rounded-lg bg-slate-900/60 border border-slate-800 flex justify-between">
                <span>Mốc 3 người:</span>
                <span className="font-bold text-cyan-300">Xuất Album Cuộc Đời PDF</span>
              </div>
              <div className="p-2.5 rounded-lg bg-slate-900/60 border border-slate-800 flex justify-between">
                <span>Mốc 5 người:</span>
                <span className="font-bold text-amber-400">Khung Thẻ Hoài Cổ Hoàng Kim</span>
              </div>
              <div className="p-2.5 rounded-lg bg-slate-900/60 border border-slate-800 flex justify-between">
                <span>Mốc 10 người:</span>
                <span className="font-bold text-rose-400">Ngoại Trang Độc Quyền Sứ Quân / Ninja</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 6. TAB: STARS SHOP */}
      {activeTab === 'shop' && (
        <div className="retro-card p-4 space-y-4">
          <div className="text-center space-y-1">
            <span className="text-3xl">⭐</span>
            <h3 className="text-base font-black text-amber-400">CỬA HÀNG TELEGRAM STARS</h3>
            <p className="text-xs text-slate-400">
              Toàn bộ gameplay cơ bản hoàn toàn miễn phí. Cửa hàng chỉ cung cấp dịch vụ xuất kỷ niệm và ngoại trang.
            </p>
          </div>

          {shopFeedback && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl text-xs text-amber-200">
              {shopFeedback}
            </div>
          )}

          <div className="space-y-3">
            {starsProducts.map((prod) => (
              <div
                key={prod.id}
                className="p-3.5 rounded-xl bg-slate-900/80 border border-slate-800 flex items-center justify-between text-xs space-x-3"
              >
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{prod.icon}</span>
                  <div>
                    <div className="flex items-center space-x-1.5">
                      <p className="font-bold text-slate-100">{prod.name}</p>
                      {prod.badge && (
                        <span className="text-[9px] px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 font-bold">
                          {prod.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-400 leading-tight">{prod.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleBuyStars(prod)}
                  disabled={purchasingProduct === prod.id}
                  className="px-3 py-2 rounded-lg font-bold retro-button whitespace-nowrap text-xs"
                >
                  {purchasingProduct === prod.id ? 'Đang tạo...' : `⭐ ${prod.starsPrice} Stars`}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 7. TAB: PROFILE */}
      {activeTab === 'profile' && (
        <div className="retro-card p-5 space-y-4">
          <div className="text-center space-y-1 border-b border-slate-800 pb-3">
            <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-tr from-cyan-500 via-indigo-600 to-amber-400 flex items-center justify-center text-2xl font-black text-white shadow-lg">
              👤
            </div>
            <h3 className="text-sm font-bold text-slate-100">{user?.firstName || user?.username}</h3>
            <p className="text-xs text-slate-400">Telegram ID: {user?.telegramId || '600094025'}</p>
          </div>

          <div className="grid grid-cols-2 gap-2 text-center text-xs">
            <div className="bg-slate-900/80 p-2.5 rounded-xl border border-slate-800">
              <span className="text-slate-400">Cấp Độ Hub</span>
              <p className="text-base font-black text-amber-400">Cấp {user?.level || 1}</p>
            </div>
            <div className="bg-slate-900/80 p-2.5 rounded-xl border border-slate-800">
              <span className="text-slate-400">Tổng Kinh Nghiệm</span>
              <p className="text-base font-black text-cyan-400">{user?.exp || 0} EXP</p>
            </div>
          </div>

          <div className="space-y-2 text-xs">
            <h4 className="font-bold text-slate-300">THÔNG TIN HỆ THỐNG:</h4>
            <div className="p-3 bg-slate-900/60 rounded-xl border border-slate-800 space-y-1 text-slate-400 text-[11px]">
              <p>• Bot Trung Tâm: <strong>@Gamengayxua_bot</strong></p>
              <p>• Hạm Đội Không Gian: <strong>Đế Chế Thiên Hà (Engine D1 Remote)</strong></p>
              <p>• Trạng Thái Máy Chủ: <strong>Trực tuyến an toàn</strong></p>
            </div>
          </div>
        </div>
      )}

      {/* 8. FIXED BOTTOM NAVIGATION BAR */}
      <nav
        className="fixed bottom-0 left-0 right-0 bg-[#070b14]/95 backdrop-blur-md border-t border-slate-800/80 z-50 shadow-2xl"
        style={{ paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))', paddingTop: '8px' }}
      >
        <div className="max-w-md mx-auto grid grid-cols-5 gap-1 text-center px-2">
          <button
            onClick={() => setActiveTab('home')}
            className={`py-1.5 rounded-xl flex flex-col items-center space-y-0.5 transition-all ${
              activeTab === 'home'
                ? 'text-cyan-400 font-bold bg-cyan-500/10'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <span className="text-xl">🏠</span>
            <span className="text-xs font-semibold">Trang Chủ</span>
          </button>
          <button
            onClick={() => setActiveTab('leaderboard')}
            className={`py-1.5 rounded-xl flex flex-col items-center space-y-0.5 transition-all ${
              activeTab === 'leaderboard'
                ? 'text-amber-400 font-bold bg-amber-500/10'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <span className="text-xl">🏆</span>
            <span className="text-xs font-semibold">Xếp Hạng</span>
          </button>
          <button
            onClick={() => setActiveTab('quests')}
            className={`py-1.5 rounded-xl flex flex-col items-center space-y-0.5 transition-all ${
              activeTab === 'quests'
                ? 'text-amber-400 font-bold bg-amber-500/10'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <span className="text-xl">🎁</span>
            <span className="text-xs font-semibold">Nhiệm Vụ</span>
          </button>
          <button
            onClick={() => setActiveTab('referral')}
            className={`py-1.5 rounded-xl flex flex-col items-center space-y-0.5 transition-all ${
              activeTab === 'referral'
                ? 'text-amber-400 font-bold bg-amber-500/10'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <span className="text-xl">🤝</span>
            <span className="text-xs font-semibold">Mời Bạn</span>
          </button>
          <button
            onClick={() => setActiveTab('shop')}
            className={`py-1.5 rounded-xl flex flex-col items-center space-y-0.5 transition-all ${
              activeTab === 'shop'
                ? 'text-cyan-400 font-bold bg-cyan-500/10'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <span className="text-xl">⭐</span>
            <span className="text-xs font-semibold">Shop Stars</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default App;
